'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import { Eye, EyeOff, MapPin, ArrowRight, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  general?: string;
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validatePassword(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  return null;
}

export default function AuthCard() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  function validate(): boolean {
    const newErrors: FormErrors = {};

    if (mode === 'register') {
      if (!name.trim() || name.trim().length < 2) {
        newErrors.name = 'Full name must be at least 2 characters';
      }
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (mode === 'register') {
      const pwError = validatePassword(password);
      if (pwError) newErrors.password = pwError;
    }

    if (mode === 'register') {
      if (!confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      if (mode === 'login') {
        await signIn(email.trim(), password);
        router.push('/seller-dashboard');
      } else {
        await signUp(email.trim(), password, { fullName: name.trim(), phone });
        setSuccessMessage('Account created! Please check your email to verify your account, then sign in.');
        setMode('login');
        setPassword('');
        setConfirmPassword('');
      }
    } catch (err: any) {
      const msg = err?.message || 'Something went wrong. Please try again.';
      if (msg.includes('Invalid login credentials') || msg.includes('invalid_credentials')) {
        setErrors({ general: 'Invalid email or password. Please try again.' });
      } else if (msg.includes('User already registered') || msg.includes('already been registered')) {
        setErrors({ general: 'An account with this email already exists. Please sign in.' });
      } else if (msg.includes('Email not confirmed')) {
        setErrors({ general: 'Please verify your email before signing in.' });
      } else {
        setErrors({ general: msg });
      }
    } finally {
      setLoading(false);
    }
  }

  function switchMode(newMode: 'login' | 'register') {
    setMode(newMode);
    setErrors({});
    setSuccessMessage('');
    setPassword('');
    setConfirmPassword('');
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8] flex">
      {/* Left: Image - hidden on mobile */}
      <div className="hidden lg:block w-1/2 relative">
        <AppImage
          src="https://img.rocket.new/generatedImages/rocket_gen_img_150f130e5-1779263042841.png"
          alt="Luxury apartment building in Bengaluru with modern architecture"
          fill
          className="object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)' }} />
        <div className="absolute bottom-12 left-12 right-12">
          <Link href="/" className="flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded bg-[#E4191B] flex items-center justify-center">
              <MapPin size={16} className="text-white" />
            </div>
            <span className="font-poppins font-700 text-white text-xl">Namma<span className="text-[#E4191B]">Price</span></span>
          </Link>
          <h2 className="font-poppins text-white mb-3" style={{ fontSize: '32px', fontWeight: 400, lineHeight: 1.3 }}>
            Find Your Perfect Property in Karnataka
          </h2>
          <p className="font-rubik text-white/70" style={{ fontWeight: 300, fontSize: '15px', lineHeight: 1.7 }}>
            Join 1.2 lakh+ users who trust NammaPrice to buy, sell, and rent properties across Karnataka.
          </p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-start lg:items-center justify-center p-5 sm:p-8 overflow-y-auto">
        <div className="w-full py-4 lg:py-0" style={{ maxWidth: '420px' }}>
          {/* Logo (mobile) */}
          <Link href="/" className="flex items-center gap-2 mb-6 lg:hidden">
            <div className="w-8 h-8 rounded bg-[#E4191B] flex items-center justify-center">
              <MapPin size={14} className="text-white" />
            </div>
            <span className="font-poppins font-700 text-[#222222] text-lg">Namma<span className="text-[#E4191B]">Price</span></span>
          </Link>

          <h1 className="font-poppins text-[#222222] mb-2" style={{ fontSize: 'clamp(22px, 5vw, 28px)', fontWeight: 400 }}>
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="font-rubik text-[#777777] mb-6 lg:mb-8 text-[14px] lg:text-[15px]" style={{ fontWeight: 300 }}>
            {mode === 'login' ? 'Sign in to your NammaPrice account' : "Join NammaPrice — it's free"}
          </p>

          {/* Mode toggle */}
          <div className="flex gap-0 mb-6 lg:mb-8 border border-[#D1D1D1] rounded overflow-hidden">
            <button
              type="button"
              onClick={() => switchMode('login')}
              className={`flex-1 py-3 text-sm font-600 font-rubik uppercase tracking-wide transition-all touch-manipulation ${mode === 'login' ? 'bg-[#E4191B] text-white' : 'bg-white text-[#555555] hover:bg-gray-50'}`}>
              Login
            </button>
            <button
              type="button"
              onClick={() => switchMode('register')}
              className={`flex-1 py-3 text-sm font-600 font-rubik uppercase tracking-wide transition-all touch-manipulation ${mode === 'register' ? 'bg-[#E4191B] text-white' : 'bg-white text-[#555555] hover:bg-gray-50'}`}>
              Register
            </button>
          </div>

          {/* Success message */}
          {successMessage && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-5">
              <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700 font-rubik">{successMessage}</p>
            </div>
          )}

          {/* General error */}
          {errors.general && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600 font-rubik">{errors.general}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-600 font-rubik text-[#555555] mb-1.5">Full Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); if (errors.name) setErrors(prev => ({ ...prev, name: undefined })); }}
                  placeholder="Enter your full name"
                  className={`search-input-houzez w-full ${errors.name ? 'border-red-400' : ''}`}
                  disabled={loading}
                  autoComplete="name"
                />
                {errors.name && <p className="text-xs text-red-500 mt-1 font-rubik">{errors.name}</p>}
              </div>
            )}

            {mode === 'register' && (
              <div>
                <label className="block text-xs font-600 font-rubik text-[#555555] mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your phone number"
                  className="search-input-houzez w-full"
                  disabled={loading}
                  autoComplete="tel"
                  inputMode="tel"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-600 font-rubik text-[#555555] mb-1.5">Email Address *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors(prev => ({ ...prev, email: undefined })); }}
                placeholder="Enter your email"
                className={`search-input-houzez w-full ${errors.email ? 'border-red-400' : ''}`}
                disabled={loading}
                autoComplete="email"
                inputMode="email"
              />
              {errors.email && <p className="text-xs text-red-500 mt-1 font-rubik">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-xs font-600 font-rubik text-[#555555] mb-1.5">Password *</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors(prev => ({ ...prev, password: undefined })); }}
                  placeholder={mode === 'register' ? 'Min 8 chars, 1 uppercase, 1 number' : 'Enter your password'}
                  className={`search-input-houzez w-full pr-10 ${errors.password ? 'border-red-400' : ''}`}
                  disabled={loading}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#777777] hover:text-[#E4191B] transition-colors w-8 h-8 flex items-center justify-center touch-manipulation"
                  aria-label={showPass ? 'Hide password' : 'Show password'}>
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 mt-1 font-rubik">{errors.password}</p>}
            </div>

            {mode === 'register' && (
              <div>
                <label className="block text-xs font-600 font-rubik text-[#555555] mb-1.5">Confirm Password *</label>
                <div className="relative">
                  <input
                    type={showConfirmPass ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: undefined })); }}
                    placeholder="Re-enter your password"
                    className={`search-input-houzez w-full pr-10 ${errors.confirmPassword ? 'border-red-400' : ''}`}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPass(!showConfirmPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#777777] hover:text-[#E4191B] transition-colors w-8 h-8 flex items-center justify-center touch-manipulation"
                    aria-label={showConfirmPass ? 'Hide password' : 'Show password'}>
                    {showConfirmPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-xs text-red-500 mt-1 font-rubik">{errors.confirmPassword}</p>}
              </div>
            )}

            {mode === 'login' && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer touch-manipulation">
                  <input type="checkbox" className="w-4 h-4 accent-[#E4191B]" />
                  <span className="text-sm font-rubik text-[#555555]">Remember me</span>
                </label>
                <a href="#" className="text-sm font-600 font-rubik text-[#E4191B] hover:underline">Forgot Password?</a>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-houzez-primary w-full flex items-center justify-center gap-2 mt-2 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation active:scale-[0.98] transition-transform"
              style={{ minHeight: '48px' }}>
              {loading ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  {mode === 'login' ? 'Signing In...' : 'Creating Account...'}
                </>
              ) : (
                <>
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                  <ArrowRight size={15} />
                </>
              )}
            </button>
          </form>

          <div className="mt-5 lg:mt-6 text-center">
            <p className="font-rubik text-[#777777] text-sm">
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button
                type="button"
                onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                className="text-[#E4191B] font-600 hover:underline touch-manipulation">
                {mode === 'login' ? 'Register' : 'Sign In'}
              </button>
            </p>
          </div>

          <div className="mt-6 lg:mt-8 pt-5 lg:pt-6 border-t border-[#E5E7EB] text-center">
            <p className="font-rubik text-[#777777] text-xs">
              By continuing, you agree to NammaPrice&apos;s{' '}
              <a href="#" className="text-[#E4191B] hover:underline">Terms of Service</a>{' '}and{' '}
              <a href="#" className="text-[#E4191B] hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}